/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useEffect, useState, useRef } from 'react';
import {
  ImageField,
  ImageInput,
  LinearProgress,
  useNotify,
  useTranslate,
} from 'react-admin';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import { useField } from 'react-final-form';
import PropTypes from 'prop-types';
import { uploadImage } from '../helper';

const useStyles = makeStyles((theme) => ({
  imageInput: {
    maxWidth: '400px',
    '& img': {
      maxWidth: '400px',
    },
  },
  removeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    minWidth: theme.spacing(2),
  },
  removeIcon: {
    color: theme.palette.error.main,
  },
  existingImageField: {
    maxWidth: '400px',
    maxHeight: '10rem',
    margin: '0.5rem',
  },
}));

const ImageUploadSelector = (props) => {
  const {
    source,
    folder,
    record,
    resource,
    setIsUploading = () => {},
    multiple = false,
    saveAsUrl = true,
  } = props;
  const [loading, setLoading] = useState(false);
  const [existingFilenames, setExistingFilenames] = useState(
    multiple ? [] : null,
  );
  const [uploadedFilenames, setUploadedFilenames] = useState(
    multiple ? [] : null,
  );
  const [edited, setEdited] = useState(false);
  const classes = useStyles(props);
  const translate = useTranslate();
  const notify = useNotify();
  const existingImagesRef = useRef();

  const {
    input: { onChange },
  } = useField(source);

  useEffect(() => {
    if (multiple) {
      onChange([...uploadedFilenames, ...existingFilenames]);
    } else {
      onChange(uploadedFilenames);
    }
  }, [uploadedFilenames]);

  useEffect(() => {
    setExistingFilenames(record[source] || (multiple ? [] : null));
  }, [record]);

  const removeUploadedImage = (filesToKeep) => {
    if (multiple) {
      const filenamesToKeep = filesToKeep.map((f) => f['Uploaded Image']);
      const updatedFilenames = uploadedFilenames.filter((uploadedFilename) =>
        filenamesToKeep.some((filenameToKeep) =>
          uploadedFilename.includes(filenameToKeep),
        ),
      );
      setUploadedFilenames(updatedFilenames);
    } else setUploadedFilenames('');
  };

  const removeExistingImage = (filenameToRemove) => {
    setEdited(true);
    if (multiple) {
      const updatedFileNames = existingFilenames.filter(
        (name) => name !== filenameToRemove,
      );
      setExistingFilenames(updatedFileNames);
      onChange(updatedFileNames);
    } else {
      setExistingFilenames('');
      onChange('');
    }
  };

  const handleChange = (input) => {
    setEdited(true);
    if (!input || (multiple && input.length < uploadedFilenames.length)) {
      if (uploadedFilenames) {
        // User clicked the delete button to remove an image they uploaded.
        removeUploadedImage(input);
      } else {
        // User tried to upload a file that's not an allowed image type.
        notify('Error: File type not allowed', 'warning');
      }
      return;
    }

    let files = [];
    if (!Array.isArray(input)) {
      files.push(input);
    } else {
      // If user selected image(s), then selected additional image(s), don't reupload the ones that were already uploaded
      const newInputs = input.filter((file) => !file['Uploaded Image']);
      files = newInputs;
    }

    setLoading(true);
    setIsUploading(true);
    Promise.all(
      files.map((file) => {
        return uploadImage(folder, file).then((filename) => {
          if (!filename) {
            notify(`There was a problem uploading ${filename}`, 'warning');
          }
          const filenameToSave = saveAsUrl
            ? `https://zippityinfra.imgix.net/${folder}/${filename}`
            : filename;
          if (multiple) {
            setUploadedFilenames((prev) => [
              ...new Set([...prev, filenameToSave]),
            ]);
          } else {
            setUploadedFilenames(filenameToSave);
          }
        });
      }),
    ).then(() => {
      setLoading(false);
      setIsUploading(false);
      if (existingImagesRef?.current)
        existingImagesRef.current.scrollIntoView();
    });
  };

  const renderSingleExistingImage = (src) => {
    const imageSource = saveAsUrl
      ? src
      : `https://zippityinfra.imgix.net/${folder}/${src}`;
    return (
      <div
        style={{
          position: 'relative',
          width: 'fit-content',
        }}
      >
        <IconButton
          className={classes.removeButton}
          onClick={() => {
            removeExistingImage(src);
          }}
          aria-label={translate('ra.action.delete')}
          title={translate('ra.action.delete')}
        >
          <RemoveCircle className={classes.removeIcon} />
        </IconButton>
        <img
          title={`Existing ${folder} image`}
          alt={`Existing ${folder} image`}
          src={imageSource}
          className={classes.existingImageField}
        />
      </div>
    );
  };

  const renderExistingImages = () => {
    if (record[source]) {
      if (multiple) {
        return (
          <div
            style={{ display: 'flex', flexWrap: 'wrap' }}
            ref={existingImagesRef}
          >
            {existingFilenames?.map((name) => {
              return renderSingleExistingImage(name);
            })}
          </div>
        );
      }
      if (!edited) {
        return renderSingleExistingImage(record[source]);
      }
    }
    return null;
  };

  return (
    <div>
      <ImageInput
        id={`upload_image_${source}`}
        source={`upload_${source}`}
        accept="image/*"
        onChange={handleChange}
        className={classes.imageInput}
        label={translate(`resources.${resource}.fields.${source}`)}
        multiple={multiple}
      >
        <ImageField
          id={`image_field_${source}`}
          source="image_field"
          title="Uploaded Image"
        />
      </ImageInput>

      {loading && (
        <div style={{ clear: 'both' }}>
          <LinearProgress />
        </div>
      )}

      {renderExistingImages()}
    </div>
  );
};

ImageUploadSelector.propTypes = {
  source: PropTypes.string.isRequired,
};

ImageUploadSelector.defaultProps = {};

export default ImageUploadSelector;
